import { FC, useEffect, useRef } from 'react'
import { Modal } from 'react-bootstrap-v5'
import { ErrorMessage, Form, Formik } from 'formik'
import * as Yup from 'yup'
import { showErrorAlert } from '../../../helpers/alert'
import { handleApiError, parseApiErrors } from '../../../utils/handleApiError'
import { KTSVG } from '../../../../_metronic/helpers'
import { UserModel } from '../../../modules/auth/models/UserModel'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '../../../../setup'
import { DatePicker } from 'antd'
import dayjs from "dayjs";
import { SelectExperienceField } from '../components/fields/SelectExperienceField'
import { MembersAutocompleteField } from '../components/fields/MembersAutocompleteField'
import { PartnersAutocompleteField } from '../components/fields/PartnersAutocompleteField'
import { createTicket, getTicket, updateTicket } from '../../../services/ticketService'
import { TicketResponse } from '../components/TicketList'

type Props = {
    show: boolean;
    onHide: () => void;
    userId?: number;
    ticketId?:number;
}

export const TicketModal: FC<Props> = ({ show, onHide, userId, ticketId = 0 }) => {
    const user = useSelector<RootState>(({auth}) => auth.user, shallowEqual) as UserModel
    const formikRef = useRef();
    const textareaRef = useRef(null);

    const setFormikValues = async (ticket:TicketResponse) => {
        await formikRef.current.setValues({
            waiting_list: userId,
            id: ticketId,
            note: ticket?.note ?? "",
            status: ticket?.status ?? "open",
            date: ticket?.createdAt ?? "",
            related_member_ids: [],
            related_experience_ids: [],
            related_partner_ids: [],
        });
    };

    useEffect(()=>{
        if(userId > 0 && ticketId > 0){
            getTicketById();
        }else{
        }
    },[ticketId])

    const getTicketById = async () => {
        let ticket:TicketResponse = await getTicket(ticketId);
        setFormikValues(ticket);
    }

    const initialValues = {
        waiting_list: userId,
        user_id: 0,
        note: "",
        date: dayjs(),
        status: "open",
        related_member_ids: [] as number[],
        related_experience_ids: [] as number[],
        related_partner_ids: [] as number[],
    };

    const validationSchema = Yup.object({
        waiting_list: Yup.number(),
        user_id: Yup.number(),
        note: Yup.string().required("Name is required").max(500, "Max 500 characters"),
        date: Yup.string().required("Date is required"),
        related_member_ids: Yup.array().of(Yup.number()),
        related_experience_ids: Yup.array().of(Yup.number()),
        related_partner_ids: Yup.array().of(Yup.number()),
    });

    const resetForm = () => {
        formikRef.current.resetForm({
            values: { 
                waiting_list: 0,
                user_id: 0,
                date: dayjs(),
                status: "open",
                related_member_ids: [],
                related_experience_ids: [],
                related_partner_ids: []
            }
        });
    }

    const onCancelHandle = () => {
        resetForm();
        onHide();
    }

    const handleSubmit = async (values) => {
        try {
            if(ticketId == 0){
                let isCreated = await createTicket({...values});
                showErrorAlert({ title: '', message: 'Your ticket has been created successfully.' });
            }else{
                let isUpdate = await updateTicket(ticketId,{...values});
                showErrorAlert({ title: '', message: 'Your ticket has been updated successfully.' });
            }
            onHide();
        } catch (error) {
            handleApiError(error, {
                onServerError: ()=>showErrorAlert({ title: 'Error', message: 'The ticket could not be created. Please try again.' }),
                onBadRequest: (data)=>{
                    let msg = parseApiErrors(data);
                    showErrorAlert({ title: 'Error', message: msg })
                }
            });
        }
    }

    return (
        <Modal show={show} centered size="lg" backdrop="static" dialogClassName="team-admin-add-member-modal">
            <Formik innerRef={formikRef} initialValues={initialValues} validationSchema={validationSchema} onSubmit={handleSubmit}>
                {(formik) => {

                    const handleMembersChange = (value:string) => {
                        formik.setFieldValue("related_member_ids", value);
                        formik.validateForm();
                    };
                    
                    const handlePartnersChange = (value:string) => {
                        formik.setFieldValue("related_member_ids", value);
                        formik.validateForm();
                    };
                    
                    const handleExperiencesChange = (value:string) => {
                        formik.setFieldValue("related_experience_ids", value);
                        formik.validateForm();
                    };

                    const handleTextAreaInput = (e) => {
                        const el = textareaRef.current;
                        el.style.height = "auto"; // resetea para medir bien
                        const lineHeight = 20;    // ajusta según tu font-size
                        const maxHeight = 5 * lineHeight;

                        el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
                    };

                    return (<Form>
                        <div className="team-admin-add-member">
                            {/* HEADER */}
                            <div className="admin-experience-details-header">
                                <h2 className="nb-invite-modal__title">
                                    {formik.values.user_id == 0 ? 'Add New' : 'Edit'} Ticket
                                </h2>
                                <div className="rm-style-3 cursor-pointer" onClick={onCancelHandle}>
                                    <KTSVG path="/media/svg/nobilis/closeX.svg" className="rm-style-4" />
                                </div>
                            </div>
                            {/* CONTENT */}
                            <div className="team-admin-add-member__card">
                                <div className="team-admin-add-member__section">
                                    <div className="team-admin-add-member__section-title">
                                        Task Information
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Note</label>
                                        <div className="tap-add-experience-2-input-line h-auto">
                                            <textarea name="note" value={formik.values.note} onChange={formik.handleChange} className="tap-add-experience-2-input textarea-autosize" onInput={handleTextAreaInput} ref={textareaRef} maxLength={500} />
                                        </div>
                                        <ErrorMessage name="note" component="div" className="tap-add-experience-text text-danger" />
                                    </div>
                                    <div className="team-admin-add-member__field">
                                        <label>Date</label>
                                        <DatePicker disabled={true}
                                            className="underline-picker"
                                            format="YYYY-MM-DD"
                                            placeholder="YYYY-MM-DD"
                                            value={
                                                formik.values.date
                                                ? dayjs(formik.values.date, 'YYYY-MM-DD')
                                                : null
                                            }
                                            onChange={(value) =>
                                                formik.setFieldValue(
                                                'date',
                                                value ? value.format('YYYY-MM-DD') : null
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="team-admin-add-member__field">
                                    <label>Status</label>
                                    <div className='w-100 p-0 tap-add-experience-2-input-line'>
                                        <select name="status" value={formik.values.status} onChange={formik.handleChange} disabled={ticketId == 0} className='tap-select-add-experience w-100 p-0' placeholder='Select an option'>
                                            <option value={'open'}>Open</option>
                                            <option value={'closed'}>Closed</option>
                                        </select>
                                    </div>
                                </div>

                                {/* ASSIGN MEMBERS */}
                                <div className="team-admin-add-member__field">
                                    <label>Related Member</label>
                                    <MembersAutocompleteField values={formik.values.related_member_ids} onChange={handleMembersChange} name='related_member_ids'/>
                                    <ErrorMessage name="related_member_ids" component="div" className="tap-add-experience-text text-danger" />
                                </div>
                                {/* ASSIGN EXPERIENCE */}
                                <div className="team-admin-add-member__field">
                                    <label>Related experience</label>
                                    <SelectExperienceField values={formik.values.related_experience_ids} onChange={handleExperiencesChange} name='related_experience_ids'/>
                                    <ErrorMessage name="related_experience_ids" component="div" className="tap-add-experience-text text-danger" />
                                </div>
                                {/* ASSIGN PARTNERS */}
                                <div className="team-admin-add-member__field">
                                    <label>Related Partners</label>
                                    <PartnersAutocompleteField values={formik.values.related_member_ids} onChange={handlePartnersChange} name='related_member_ids'/>
                                    <ErrorMessage name="related_member_ids" component="div" className="tap-add-experience-text text-danger" />
                                </div>
                            </div>

                            {/* FOOTER */}
                            <div className="team-admin-add-member__footer">
                                <button type='button' className="decline-experience__btn decline-experience__btn--secondary" onClick={onCancelHandle}>
                                    Cancel
                                </button>

                                <button type='submit' className="decline-experience__btn decline-experience__btn--primary">
                                    {formik.values.user_id == 0 ? 'Add' : 'Edit'} Task
                                    &nbsp;<KTSVG path='/media/svg/nobilis/vector02.svg' />
                                </button>
                            </div>
                        </div>
                    </Form>);
                }}
            </Formik>
        </Modal>
    )
}
